import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import {Select} from '@/components/tailwind/select';
import {Field, Label} from '@/components/tailwind/fieldset';
import ValidationAlert from '@/components/validation-alert';
import {SpecialAssignmentService, CreateSpecialAssignmentBookingRequest} from '@/services/special-assignment-services';
import {useEffect, useMemo, useState} from 'react';

interface SpecialAssignmentBookingDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (request: CreateSpecialAssignmentBookingRequest) => Promise<void>;
    selectedMemberIds: string[];
    availableServices: SpecialAssignmentService[];
    preSelectedServiceId?: string;
}

function calculatePoints(service: SpecialAssignmentService, startTime?: string, endTime?: string): number | null {
    if (service.isFixedPoints) {
        return service.pointsPerUnit;
    }
    if (!startTime || !endTime) {
        return null;
    }
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const durationMinutes = endMinutes - startMinutes;
    if (durationMinutes <= 0) {
        return null;
    }
    return Math.ceil(durationMinutes / 60) * service.pointsPerUnit;
}

function formatDateForDisplay(isoDate: string): string {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}.${month}.${year}`;
}

const bookingSchema = z.object({
    serviceId: z.string().min(1, 'specialAssignments.bookingDialog.errors.serviceRequired'),
    activityDate: z.string().min(1, 'specialAssignments.bookingDialog.errors.dateRequired'),
    startTime: z.string().min(1, 'specialAssignments.bookingDialog.errors.startTimeRequired'),
    endTime: z.string().min(1, 'specialAssignments.bookingDialog.errors.endTimeRequired'),
}).superRefine((data, ctx) => {
    if (data.startTime && data.endTime && data.endTime <= data.startTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'specialAssignments.bookingDialog.errors.endBeforeStart',
            path: ['endTime'],
        });
    }
});

type BookingFormData = z.infer<typeof bookingSchema>;

export default function SpecialAssignmentBookingDialog({
    isOpen,
    onClose,
    onConfirm,
    selectedMemberIds,
    availableServices,
    preSelectedServiceId,
}: SpecialAssignmentBookingDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const defaultValues = useMemo(() => ({
        serviceId: preSelectedServiceId ?? (availableServices.length === 1 ? availableServices[0].id : ''),
        activityDate: today,
        startTime: '',
        endTime: '',
    }), [preSelectedServiceId, availableServices, today]);

    const {
        register,
        handleSubmit,
        control,
        watch,
        setError,
        formState: {errors},
        reset,
    } = useForm<BookingFormData>({
        resolver: zodResolver(bookingSchema),
        defaultValues,
    });

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
            setIsSubmitting(false);
        }
    }, [isOpen, reset, defaultValues]);

    const watchedServiceId = watch('serviceId');
    const watchedDate = watch('activityDate');
    const watchedStartTime = watch('startTime');
    const watchedEndTime = watch('endTime');

    const selectedService = availableServices.find(s => s.id === watchedServiceId);
    const previewPoints = selectedService
        ? calculatePoints(selectedService, watchedStartTime, watchedEndTime)
        : null;

    async function onSubmit(data: BookingFormData) {
        if (!selectedService) {
            setError('serviceId', {message: 'specialAssignments.bookingDialog.errors.serviceRequired'});
            return;
        }

        setIsSubmitting(true);
        try {
            await onConfirm({
                serviceId: data.serviceId,
                memberIds: selectedMemberIds,
                activityDate: data.activityDate,
                startTime: data.startTime,
                endTime: data.endTime,
            });
            handleClosing();
        } catch {
            setError('root', {message: t('specialAssignments.toast.error.message')});
            setIsSubmitting(false);
        }
    }

    function handleClosing() {
        reset();
        onClose();
    }

    const count = selectedMemberIds.length;
    const showPreview = selectedService !== undefined && watchedDate && previewPoints !== null;

    return (
        <Dialog open={isOpen} onClose={handleClosing}>
            <DialogTitle>
                {t('specialAssignments.bookingDialog.title')}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root.message || tCommon('serverError.unknown')}/>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Field>
                        <Label>{t('specialAssignments.bookingDialog.fields.service.label')}</Label>
                        <Controller
                            name="serviceId"
                            control={control}
                            render={({field}) => (
                                <Select
                                    {...field}
                                    invalid={Boolean(errors.serviceId)}
                                    data-testid="booking-service-select"
                                >
                                    <option value="">
                                        {t('specialAssignments.bookingDialog.fields.service.placeholder')}
                                    </option>
                                    {availableServices.map(service => (
                                        <option key={service.id} value={service.id}>
                                            {service.name}
                                        </option>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.serviceId && (
                            <ValidationAlert message={t(errors.serviceId.message || 'specialAssignments.bookingDialog.errors.serviceRequired')}/>
                        )}
                    </Field>

                    <Field>
                        <Label>{t('specialAssignments.bookingDialog.fields.date.label')}</Label>
                        <Input
                            type="date"
                            {...register('activityDate')}
                            invalid={Boolean(errors.activityDate)}
                            data-testid="booking-date-input"
                        />
                        {errors.activityDate && (
                            <ValidationAlert message={t(errors.activityDate.message || 'specialAssignments.bookingDialog.errors.dateRequired')}/>
                        )}
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                        <Field>
                            <Label>{t('specialAssignments.bookingDialog.fields.startTime.label')}</Label>
                            <Input
                                type="time"
                                {...register('startTime')}
                                invalid={Boolean(errors.startTime)}
                                data-testid="booking-start-time-input"
                            />
                            {errors.startTime && (
                                <ValidationAlert message={t(errors.startTime.message || 'specialAssignments.bookingDialog.errors.startTimeRequired')}/>
                            )}
                        </Field>

                        <Field>
                            <Label>{t('specialAssignments.bookingDialog.fields.endTime.label')}</Label>
                            <Input
                                type="time"
                                {...register('endTime')}
                                invalid={Boolean(errors.endTime)}
                                data-testid="booking-end-time-input"
                            />
                            {errors.endTime && (
                                <ValidationAlert message={t(errors.endTime.message || 'specialAssignments.bookingDialog.errors.endBeforeStart')}/>
                            )}
                        </Field>
                    </div>

                    {showPreview && (
                        <div
                            className="rounded-md bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 text-sm text-blue-800 dark:text-blue-200"
                            data-testid="booking-preview"
                        >
                            {count === 1
                                ? t('specialAssignments.bookingDialog.previewSingular', {
                                    points: previewPoints,
                                    service: selectedService!.name,
                                    date: formatDateForDisplay(watchedDate),
                                })
                                : t('specialAssignments.bookingDialog.preview', {
                                    count,
                                    points: previewPoints,
                                    service: selectedService!.name,
                                    date: formatDateForDisplay(watchedDate),
                                })
                            }
                        </div>
                    )}

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                            data-testid="booking-cancel-button"
                        >
                            {t('specialAssignments.bookingDialog.buttons.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="booking-confirm-button"
                        >
                            {t('specialAssignments.bookingDialog.buttons.confirm')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
